/** @format */

import {
  useActiveFeatureFlags,
  useFeatureFlagPayload,
  useFeatureFlagVariantKey,
} from "posthog-js/react";
import { useRef } from "react";
import {
  asSanctioningBody,
  type SanctioningBodySlug,
  sortByCanonicalOrder,
} from "@/utils/sanctioningBody";

/**
 * Wrapper around PostHog's useActiveFeatureFlags that returns a stable
 * array reference when the flag values haven't changed.
 *
 * PostHog's useActiveFeatureFlags() calls setFeatureFlags(flags) with a new
 * array reference every time onFeatureFlags fires, triggering re-renders in
 * all subscribers even when the actual flag values are identical.
 */
export function useStableFeatureFlags(): string[] {
  const rawFlags = useActiveFeatureFlags();
  const flags = rawFlags ?? [];
  const prevRef = useRef(flags);

  if (
    flags.length !== prevRef.current.length ||
    flags.some((f, i) => f !== prevRef.current[i])
  ) {
    prevRef.current = flags;
  }

  return prevRef.current;
}

export function useNotificationsEnabled(): boolean {
  const flags = useStableFeatureFlags();
  return flags.includes("notifications");
}

export function useRevalidationRacerEnabled(): boolean {
  const flags = useStableFeatureFlags();
  return flags.includes("revalidation-racer");
}

/**
 * PostHog flag `accessible-sanctioning-bodies` (id 666794) — multivariate
 * flag where each variant carries a JSON payload that's the `string[]` of
 * sanctioning bodies the user may see. Cohort → variant → payload:
 *
 *   Staff cohort (55626)     → `nascar-arca-grcup` → `["nascar","arca","grcup"]`
 *   Industry cohort (226086) → `nascar-arca`       → `["nascar","arca"]`
 *   Everyone else            → `nascar`            → `["nascar"]`
 *
 * Adding a new tier is a pure PostHog change — add a release condition
 * with its variant + payload. No FE deploy needed.
 *
 * We call BOTH `useFeatureFlagVariantKey` and `useFeatureFlagPayload`
 * because the posthog-js/react docs require it: the variant hook is what
 * fires the `$feature_flag_called` exposure event for analytics
 * correctness (https://posthog.com/docs/libraries/react – "Always use
 * this with useFeatureFlagEnabled or useFeatureFlagVariantKey"). It does
 * NOT keep the payload fresh under toolbar overrides — that's a
 * different, unrelated concern handled below.
 *
 * Fallback to `["nascar"]` only fires when PostHog returns nothing
 * (outage, pre-identify hydration window) — a NASCAR-only home beats a
 * 404.
 *
 * --- Testing this flag without a deploy ---
 *
 * The PostHog **toolbar** UI does not work for this flag. It changes the
 * variant locally but leaves the payload stale (open SDK bug
 * https://github.com/PostHog/posthog-js/issues/2101 and
 * https://github.com/PostHog/posthog-js/issues/1830). `getFeatureFlag()`
 * reflects the override, `getFeatureFlagPayload()` does not — and we
 * read the payload, so the UI doesn't react.
 *
 * Two workarounds:
 *
 *   1. Easiest — flip cohort membership in PostHog (Cohorts → Staff or
 *      Industry, add/remove yourself, refresh). Exercises the real
 *      /decide/ path.
 *
 *   2. Dev-console manual override that sets BOTH variant and payload
 *      (the `flags + payloads` API works around posthog-js#2101 by
 *      providing the matching payload directly):
 *
 *        posthog.featureFlags.overrideFeatureFlags({
 *          flags:    { 'accessible-sanctioning-bodies': 'nascar-arca' },
 *          payloads: { 'accessible-sanctioning-bodies': ['nascar','arca'] },
 *        });
 */
const ACCESSIBLE_SANCTIONING_BODIES_FLAG = "accessible-sanctioning-bodies";
const SANCTIONING_BODY_FALLBACK: SanctioningBodySlug[] = ["nascar"];

export function useAccessibleSanctioningBodies(): SanctioningBodySlug[] {
  // Variant call is the exposure-event trigger that keeps the payload in
  // sync; the variant value itself isn't read here (PostHog defines the
  // payload), but the call is load-bearing.
  useFeatureFlagVariantKey(ACCESSIBLE_SANCTIONING_BODIES_FLAG);
  const payload = useFeatureFlagPayload(ACCESSIBLE_SANCTIONING_BODIES_FLAG);
  if (Array.isArray(payload)) {
    const bodies = payload
      .map(asSanctioningBody)
      .filter((b): b is SanctioningBodySlug => b !== null);
    if (bodies.length > 0) {
      return sortByCanonicalOrder(bodies);
    }
  }
  return SANCTIONING_BODY_FALLBACK;
}
